'use client';

import { createContext, useContext } from 'react';

import { FloatingActionButtons } from '@/components/molecules/floating-action-buttons';
import { useServiceTypeTemplatesCtx } from '@/components/providers/service-templates-provider';
import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { Skeleton } from '@/components/ui/skeleton';
import { useEditableStateUpdates } from '@/hooks/use-editable-state';

type ApiServiceTypeTemplate = ApiSchemas['ServiceTypeTemplateRead'];

function transform(template: ApiServiceTypeTemplate) {
  const { skills, ...rest } = template;
  return {
    ...rest,
    skill_ids: skills.map((skill) => skill.skill_id) || [],
  };
}

type ServiceTypeTemplate = ReturnType<typeof transform>;

interface Context {
  serviceTypeTemplate: ServiceTypeTemplate;
  update: (v: Partial<ServiceTypeTemplate>) => void;
}

const ServiceTypeTemplateContext = createContext<Context | null>(null);

export function useServiceTypeTemplateCtx() {
  const ctx = useContext(ServiceTypeTemplateContext);
  if (!ctx) {
    throw new Error('useServiceTypeTemplateCtx must be used within a ServiceTypeTemplateProvider');
  }
  return ctx;
}

export function ServiceTypeTemplateProvider({ id, children }: { id: string; children: React.ReactNode }) {
  const { serviceTypeTemplatesById, isLoading } = useServiceTypeTemplatesCtx();
  const serviceTypeTemplate = serviceTypeTemplatesById.get(id);

  if (isLoading) {
    return (
      <div className='p-4'>
        <Skeleton className='h-[90dvh] w-full' />
      </div>
    );
  }

  if (!serviceTypeTemplate) {
    return (
      <div className='p-4'>
        <div className='flex h-[90dvh] w-full items-center justify-center rounded-lg bg-muted/50 text-sm font-medium text-muted-foreground'>
          Service type templat not found
        </div>
      </div>
    );
  }

  return <Provider serviceTypeTemplate={serviceTypeTemplate}>{children}</Provider>;
}

function Provider({
  serviceTypeTemplate,
  children,
}: {
  serviceTypeTemplate: ApiServiceTypeTemplate;
  children: React.ReactNode;
}) {
  const { api } = useApiV1Ctx();
  const state = useEditableStateUpdates(serviceTypeTemplate, transform);

  const mutation = api.serviceTypeTemplates.updateServiceTypeTemplatesTemplateIdPatch.useMutation(
    {
      path: { template_id: serviceTypeTemplate.id },
    },
    {
      onSuccess: (data) => {
        state.reset(data);
      },
    }
  );

  const onSave = () => {
    mutation.mutate(state.updates);
  };

  return (
    <ServiceTypeTemplateContext.Provider value={{ serviceTypeTemplate: state.value, update: state.setUpdates }}>
      {children}
      {state.isDirty && (
        <FloatingActionButtons
          onSave={onSave}
          onDiscard={() => state.reset(serviceTypeTemplate)}
          disabled={mutation.isPending}
        />
      )}
    </ServiceTypeTemplateContext.Provider>
  );
}
